import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/crmApi";
import EnrichmentRunsConsole from "@/crm/components/EnrichmentRunsConsole";
import { useAuth } from "@/context/CrmAuth";
import { PageHeader, Loader, KpiCard } from "@/crm/ui";
import { toast } from "sonner";
import { Landmark, MapPin, Globe, Mail, Phone, BookUser, Bot, Sparkles, Store, CheckCircle2, XCircle, Clock, Play, PawPrint, BookOpen, Search, X, Building2, KeyRound, Trash2 } from "lucide-react";

const PROVIDER_META = {
  sirene: { label: "Registre public (SIRENE / RNE)", icon: Landmark },
  maps: { label: "Géolocalisation (BAN + Google Maps)", icon: MapPin },
  google_business: { label: "Fiche Google Business (clé Places requise)", icon: Store },
  website: { label: "Détection site web", icon: Globe },
  email: { label: "Email lu sur le site (avec preuve)", icon: Mail },
  phone: { label: "Téléphone lu sur le site (avec preuve)", icon: Phone },
  mairie_com: { label: "Annuaire Mairie.com (téléphones)", icon: BookUser },
  bottin: { label: "Annuaire Bottin.fr (téléphones, par activité et commune)", icon: BookUser },
  toiletteur_chien_chat: { label: "Annuaire Toiletteur-chien-chat.fr (salons de toilettage)", icon: PawPrint },
  zouaf: { label: "Annuaire Zouaf.com (professionnels de l'animal)", icon: PawPrint },
  annuaire_118712: { label: "Annuaire 118 712 (téléphones — accès souvent bloqué par le site)", icon: BookOpen },
};

// L'IA coûte à l'appel : un lancement en masse est volontairement borné, le
// serveur refuse au-delà (routers/ai_enrichment.py).
const MAX_IA = 20;

// Préréglages du sélecteur de sources : un besoin, pas une liste technique.
// Chaque préréglage est une fonction du catalogue, pour suivre les sources
// ajoutées demain sans retoucher l'écran.
const utilisables = (cat) => cat.filter((p) => p.enabled && !p.requires_key);
const fournit = (p, champ) => (p.fields || []).some((f) => f.field === champ);
export const SOURCE_PRESETS = [
  { id: "contact", label: "Contact complet", pick: (cat) => utilisables(cat).map((p) => p.name) },
  { id: "telephone", label: "Téléphone", pick: (cat) => utilisables(cat).filter((p) => fournit(p, "telephone") || p.category === "site").map((p) => p.name) },
  { id: "email", label: "Email", pick: (cat) => utilisables(cat).filter((p) => fournit(p, "email") || p.name === "website").map((p) => p.name) },
  { id: "site", label: "Site web", pick: (cat) => utilisables(cat).filter((p) => fournit(p, "site_web")).map((p) => p.name) },
  { id: "annuaires", label: "Annuaires seuls", pick: (cat) => utilisables(cat).filter((p) => p.category === "annuaires").map((p) => p.name) },
  { id: "tout", label: "Tout", pick: (cat) => cat.filter((p) => p.enabled).map((p) => p.name) },
  { id: "rien", label: "Rien", pick: () => [] },
];

const BUDGETS = [["rapide", "Recherche rapide"], ["standard", "Recherche standard"],
  ["approfondie", "Recherche approfondie"]];

// Que faire des fiches déjà enrichies ? Par défaut on continue sur le reste ;
// tout le reste est un choix explicite (le serveur ne décide rien tout seul).
const POLICIES = [
  ["skip", "Continuer sur les fiches jamais enrichies", "Les fiches déjà passées sont ignorées : chaque lancement avance dans la base."],
  ["providers", "Reprendre celles jamais passées par les sources cochées", "Une fiche enrichie par Maps mais jamais par Email est reprise si Email est coché."],
  ["stale", "Reprendre celles enrichies il y a plus de…", "Rafraîchir les fiches anciennes ; les jamais enrichies passent en premier."],
  ["all", "Tout reprendre, même déjà enrichies (forcer)", "Aucun filtre sur le suivi : utile après un changement de sources ou de règles."],
];

/** Paramètres de ciblage envoyés à GET /companies pour une politique donnée. */
export function policyParams(policy, staleDays, providers, mode) {
  if (policy === "skip") return { enriched: "never" };
  if (policy === "providers") {
    return { missing_providers: mode === "ia" ? "ia" : (providers.join(",") || "ia") };
  }
  if (policy === "stale") return { enriched_before_days: String(staleDays || 90), sort: "last_enrichment_at" };
  return {};
}

export default function Enrichment() {
  const { user } = useAuth();
  const canPurge = user?.role === "admin" || user?.role === "manager";
  const [mode, setMode] = useState("classique"); // classique | ia
  const [providers, setProviders] = useState(["sirene", "maps", "website", "email"]);
  const [budget, setBudget] = useState("standard");
  const [jobs, setJobs] = useState({ jobs: [], stats: {} });
  const [batch, setBatch] = useState(null);
  const [aiJobs, setAiJobs] = useState(null); // {ids: [], done, total, review}
  const [scope, setScope] = useState("no_website");
  const [catalog, setCatalog] = useState(null);            // {providers, categories}
  const [picked, setPicked] = useState([]);                // entreprises choisies à la main [{id, nom, ville}]
  const [policy, setPolicy] = useState("skip");
  const [staleDays, setStaleDays] = useState(90);
  const [coverage, setCoverage] = useState(null);   // {total, enriched, never, by_provider}
  const [preview, setPreview] = useState(null);     // nombre de fiches ciblées
  const [running, setRunning] = useState(false);
  const [consoleKey, setConsoleKey] = useState(0);        // la console se rafraîchit à chaque fin de lancement
  const timer = useRef(null);

  const loadJobs = () => api.get("/enrichment/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  // Jobs restés « en cours » : les expliquer, et permettre de les refermer.
  const reconcile = async (force) => {
    if (force && !window.confirm("Arrêter tous les enrichissements en cours ? Les fiches déjà traitées sont conservées.")) return;
    try {
      const { data } = await api.post("/enrichment/jobs/reconcile", { force });
      toast.success(`${data.jobs.length} job(s) refermé(s)${data.batches.length ? ` · ${data.batches.length} batch(s) arrêté(s)` : ""}`);
      loadJobs(); setConsoleKey((k) => k + 1);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Action impossible"); }
  };
  const purgeJobs = async () => {
    if (!window.confirm("Purger les derniers jobs terminés ?")) return;
    try { await api.delete("/enrichment/jobs"); toast.success("Derniers jobs purgés"); loadJobs(); }
    catch { toast.error("Purge impossible"); }
  };
  const loadCoverage = () => api.get("/enrichment/coverage").then(({ data }) => setCoverage(data)).catch(() => {});
  useEffect(() => {
    loadJobs(); loadCoverage();
    api.get("/enrichment/catalog").then(({ data }) => setCatalog(data)).catch(() => setCatalog({ providers: [], categories: [] }));
  }, []);

  const toggle = (p) => setProviders((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);

  // Ciblage = portée (site / email / toutes) × politique sur le déjà-enrichi.
  const targetParams = () => {
    const params = { ...policyParams(policy, staleDays, providers, mode) };
    if (scope === "no_website") params.has_website = "false";
    if (scope === "no_email") params.has_email = "false";
    return params;
  };
  const cibles = async (limit) => {
    // Choix explicite : ces fiches-là, sans filtre ni politique — l'utilisateur
    // a décidé, y compris de repasser sur du déjà-enrichi.
    if (scope === "selection") return picked.slice(0, limit).map((c) => c.id);
    const { data } = await api.get("/companies", { params: { ...targetParams(), limit, page: 1 } });
    return data.items.map((c) => c.id);
  };

  // Aperçu : combien de fiches la cible actuelle représente, avant de lancer.
  useEffect(() => {
    if (scope === "selection") { setPreview(picked.length); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/companies", { params: { ...targetParams(), limit: 1, page: 1 } })
        .then(({ data }) => { if (!cancelled) setPreview(data.total ?? null); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, policy, staleDays, providers, mode, picked.length]);

  const launch = async () => {
    if (!providers.length) return toast.error("Sélectionnez au moins un fournisseur");
    setRunning(true);
    const ids = await cibles(200);
    if (!ids.length) { toast.info("Aucune entreprise correspondante"); setRunning(false); return; }
    const { data: b } = await api.post("/enrichment/batch", { company_ids: ids, providers });
    toast.success(`Enrichissement lancé sur ${ids.length} entreprises`);
    setBatch({ id: b.batch_id, total: b.total, done: 0, success: 0, failed: 0, status: "running" });
    timer.current = setInterval(async () => {
      const { data: p } = await api.get(`/enrichment/batch/${b.batch_id}`);
      setBatch({ id: b.batch_id, ...p });
      if (p.status === "done") { clearInterval(timer.current); setRunning(false); loadJobs(); loadCoverage(); setConsoleKey((k) => k + 1); toast.success("Enrichissement terminé"); }
    }, 1500);
  };

  const launchIA = async () => {
    setRunning(true);
    try {
      const ids = await cibles(MAX_IA);
      if (!ids.length) { toast.info("Aucune entreprise correspondante"); setRunning(false); return; }
      const { data } = await api.post("/ai-enrichment/jobs", { company_ids: ids, budget });
      const jobIds = data.jobs.map((j) => j.job_id);
      toast.success(`Enrichissement IA lancé sur ${jobIds.length} entreprise(s)`);
      setAiJobs({ ids: jobIds, total: jobIds.length, done: 0, review: 0, failed: 0 });
      timer.current = setInterval(async () => {
        const { data: l } = await api.get("/ai-enrichment/jobs", { params: { limit: 100 } });
        const miens = l.jobs.filter((j) => jobIds.includes(j.id));
        const finis = miens.filter((j) => ["DONE", "FAILED", "CANCELLED"].includes(j.status));
        setAiJobs({
          ids: jobIds, total: jobIds.length, done: finis.length,
          failed: miens.filter((j) => j.status === "FAILED").length,
          review: miens.filter((j) => j.result?.status === "NEEDS_REVIEW").length,
        });
        if (finis.length === jobIds.length) {
          clearInterval(timer.current); setRunning(false); loadCoverage(); setConsoleKey((k) => k + 1);
          toast.success("Enrichissement IA terminé — les cas ambigus attendent votre revue sur les fiches");
        }
      }, 2500);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Lancement IA impossible");
      setRunning(false);
    }
  };

  useEffect(() => () => clearInterval(timer.current), []);

  if (!jobs) return <Loader />;
  const pct = batch?.total ? Math.round((batch.done / batch.total) * 100) : 0;

  return (
    <div>
      <PageHeader title="Enrichissement" subtitle="Complétez automatiquement les fiches entreprises" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <KpiCard label="Succès" value={jobs.stats.SUCCESS || 0} icon={CheckCircle2} accent="emerald" />
        <KpiCard label="Échecs" value={jobs.stats.FAILED || 0} icon={XCircle} accent="rose" />
        <KpiCard label="En cours" value={jobs.stats.RUNNING || 0} icon={Clock} accent="amber" />
        <KpiCard label="En attente" value={jobs.stats.PENDING || 0} icon={Clock} accent="sky" />
      </div>
      {(jobs.stuck || []).length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="stuck-jobs">
          <div className="flex flex-wrap items-center gap-2">
            <Clock size={16} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-900">{jobs.stuck.length} enrichissement{jobs.stuck.length > 1 ? "s" : ""} « en cours »</span>
            <span className="text-xs text-amber-800">
              {jobs.stuck.some((j) => j.batch_alive) ? "Au moins un batch tourne encore." : "Aucun batch ne tourne : ces jobs ont été interrompus."}
            </span>
            {canPurge && (
              <span className="ml-auto flex gap-2">
                {jobs.stuck.some((j) => !j.batch_alive) && (
                  <button data-testid="stuck-reconcile" onClick={() => reconcile(false)}
                    className="rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100">
                    Refermer les interrompus
                  </button>
                )}
                <button data-testid="stuck-force" onClick={() => reconcile(true)}
                  className="rounded-lg bg-rose-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-rose-700">
                  Forcer l'arrêt de tout
                </button>
              </span>
            )}
          </div>
          <ul className="mt-2 space-y-1 text-[12.5px] text-amber-900">
            {jobs.stuck.map((j) => (
              <li key={j.id} data-testid={`stuck-${j.id}`}>
                <b>{j.company_name || j.company_id}</b> · {j.provider} · depuis {j.age_minutes} min · batch {j.batch_alive ? "actif" : `mort (${j.batch_status || "inconnu"})`} — {j.why}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Ce que l'enrichissement a réellement apporté : des fiches qui ont
          maintenant un contact qu'elles n'avaient pas, par type de contact. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="enrich-completed">
        <KpiCard label="Fiches complétées" value={coverage?.completed?.total ?? "…"} icon={Sparkles} accent="indigo" />
        <KpiCard label="Téléphone obtenu" value={coverage?.completed?.telephone ?? "…"} icon={Phone} accent="emerald" />
        <KpiCard label="Email obtenu" value={coverage?.completed?.email ?? "…"} icon={Mail} accent="sky" />
        <KpiCard label="Site web obtenu" value={coverage?.completed?.site_web ?? "…"} icon={Globe} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Lancer un enrichissement</h3>

          {/* Mode : pipeline déterministe, ou agent IA (plan V3 §37) */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button data-testid="mode-classique" onClick={() => setMode("classique")}
              className={`flex items-center justify-center gap-2 py-2 rounded-lg border text-sm ${mode === "classique" ? "border-indigo-300 bg-indigo-50 text-indigo-700 font-medium" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              <Sparkles size={15} /> Classique
            </button>
            <button data-testid="mode-ia" onClick={() => setMode("ia")}
              className={`flex items-center justify-center gap-2 py-2 rounded-lg border text-sm ${mode === "ia" ? "border-violet-300 bg-violet-50 text-violet-700 font-medium" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              <Bot size={15} /> Avec IA
            </button>
          </div>

          {mode === "classique" ? (
            <SourcePicker catalog={catalog} selected={providers} onChange={setProviders} toggle={toggle} />
          ) : (
            <div className="mb-4">
              <div className="space-y-2 mb-3">
                {BUDGETS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                    <input type="radio" checked={budget === key} onChange={() => setBudget(key)} />
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400">
                L'agent IA orchestre toutes les sources activées, compare les preuves et
                soumet les cas ambigus à votre revue. Limité à {MAX_IA} fiches par lancement.
              </p>
            </div>
          )}

          <label className="text-xs text-slate-500">Cible</label>
          <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full mt-1 mb-4 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white" data-testid="enrich-scope">
            <option value="no_website">Entreprises sans site web</option>
            <option value="no_email">Entreprises sans email</option>
            <option value="all">Toutes (max {mode === "ia" ? MAX_IA : 200})</option>
            <option value="selection">Entreprises choisies à la main{picked.length ? ` (${picked.length})` : ""}</option>
          </select>
          {scope === "selection" && <CompanyPicker picked={picked} onChange={setPicked} max={mode === "ia" ? MAX_IA : 200} />}

          {/* Suivi du déjà-enrichi : le lanceur continue là où il s'est arrêté,
              sauf si l'utilisateur veut revenir en arrière — par source, par
              ancienneté, ou sur tout. */}
          {scope !== "selection" && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="enrich-policy">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-semibold text-slate-600">Fiches déjà enrichies</span>
              {coverage && (
                <span className="text-[11px] text-slate-500" data-testid="enrich-coverage">
                  <b className="text-emerald-700">{coverage.enriched}</b> déjà enrichies · <b className="text-indigo-700">{coverage.never}</b> jamais · {coverage.total} au total
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {POLICIES.map(([key, label, help]) => (
                <label key={key} className={`flex items-start gap-2.5 rounded-lg border px-2.5 py-2 cursor-pointer ${policy === key ? "border-indigo-300 bg-white" : "border-transparent hover:bg-white"}`}>
                  <input type="radio" name="enrich-policy" data-testid={`policy-${key}`} checked={policy === key} onChange={() => setPolicy(key)} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-sm text-slate-700">
                      {label}
                      {key === "stale" && (
                        <>
                          {" "}
                          <input type="number" min="1" max="3650" value={staleDays} data-testid="policy-stale-days"
                            onChange={(e) => setStaleDays(Math.max(1, Number(e.target.value) || 1))} onClick={() => setPolicy("stale")}
                            className="w-16 rounded border border-slate-300 px-1.5 py-0.5 text-sm text-center" />
                          {" "}jours
                        </>
                      )}
                    </span>
                    <span className="block text-[11px] text-slate-400">{help}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500" data-testid="enrich-preview">
              {preview == null ? "…"
                : preview === 0 ? "Aucune fiche ne correspond à cette cible."
                : `${preview} fiche${preview > 1 ? "s" : ""} correspond${preview > 1 ? "ent" : ""} · ${Math.min(preview, mode === "ia" ? MAX_IA : 200)} traitée${Math.min(preview, mode === "ia" ? MAX_IA : 200) > 1 ? "s" : ""} à ce lancement`}
            </p>
          </div>
          )}
          <button data-testid="launch-enrichment" onClick={mode === "ia" ? launchIA : launch} disabled={running}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm disabled:opacity-60 ${mode === "ia" ? "bg-violet-600 hover:bg-violet-700" : "bg-indigo-600 hover:bg-indigo-700"}`}>
            <Play size={16} /> {mode === "ia" ? "Lancer l'enrichissement IA" : "Lancer l'enrichissement"}
          </button>
          {mode === "classique" && batch && (
            <div className="mt-4" data-testid="enrichment-progress">
              <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{batch.done} / {batch.total}</span><span>{pct}%</span></div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-600 transition-all" style={{ width: `${pct}%` }} /></div>
              <div className="flex gap-4 mt-2 text-xs"><span className="text-emerald-600">Succès : {batch.success}</span><span className="text-rose-600">Échecs : {batch.failed}</span></div>
            </div>
          )}
          {mode === "ia" && aiJobs && (
            <div className="mt-4" data-testid="ai-enrichment-progress">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>{aiJobs.done} / {aiJobs.total}</span>
                <span>{aiJobs.total ? Math.round((aiJobs.done / aiJobs.total) * 100) : 0}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-violet-600 transition-all" style={{ width: `${aiJobs.total ? (aiJobs.done / aiJobs.total) * 100 : 0}%` }} /></div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-violet-600">À vérifier : {aiJobs.review}</span>
                <span className="text-rose-600">Échecs : {aiJobs.failed}</span>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <EnrichmentRunsConsole refreshKey={consoleKey} canPurge={canPurge} />
          <details className="bg-white rounded-xl border border-slate-200 p-5">
            <summary className="cursor-pointer font-heading font-semibold text-slate-800">
              Derniers jobs ({jobs.jobs.length})
              <span className="ml-2 text-[11px] font-normal text-slate-400">purgés à chaque lancement</span>
              {canPurge && jobs.jobs.length > 0 && (
                <button onClick={(e) => { e.preventDefault(); purgeJobs(); }} data-testid="jobs-purge"
                  className="ml-3 inline-flex items-center gap-1 text-xs font-normal text-rose-600 hover:text-rose-800"><Trash2 size={12} /> Purger</button>
              )}
            </summary>
            <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto">
              {jobs.jobs.length === 0 ? <p className="text-sm text-slate-400">Aucun job</p> : jobs.jobs.slice(0, 40).map((j) => (
                <div key={j.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50">
                  <span className="text-slate-500 text-xs font-mono">{j.provider}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-md ${j.status === "SUCCESS" ? "bg-emerald-100 text-emerald-700" : j.status === "FAILED" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{j.status}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sélecteur de sources : par besoin (préréglages) et par famille (catégories).
// ---------------------------------------------------------------------------
const NIVEAU_CLS = { "élevée": "bg-emerald-50 text-emerald-700", "moyenne": "bg-amber-50 text-amber-700", "faible": "bg-slate-100 text-slate-500" };

function SourcePicker({ catalog, selected, onChange, toggle }) {
  const providers = catalog?.providers || [];
  const categories = catalog?.categories || [];
  if (!catalog) return <p className="mb-4 text-xs text-slate-400">Chargement des sources…</p>;
  if (providers.length === 0) {
    // Catalogue indisponible : la liste figée reste utilisable.
    return (
      <div className="space-y-2 mb-4">
        {Object.entries(PROVIDER_META).map(([key, m]) => (
          <label key={key} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid={`provider-${key}`}>
            <input type="checkbox" checked={selected.includes(key)} onChange={() => toggle(key)} />
            <m.icon size={16} className="text-indigo-500" />
            <span className="text-sm text-slate-700">{m.label}</span>
          </label>
        ))}
      </div>
    );
  }
  const byCat = categories.map((c) => ({ ...c, items: providers.filter((p) => p.category === c.id) })).filter((c) => c.items.length);
  const setCategory = (items, on) => {
    const names = items.filter((p) => p.enabled).map((p) => p.name);
    onChange(on ? Array.from(new Set([...selected, ...names])) : selected.filter((n) => !names.includes(n)));
  };
  // « Je veux » se cumule : Téléphone + Email = l'union des deux. Une puce est
  // active quand toutes ses sources sont cochées (donc aussi après un choix à
  // la main) ; la désactiver retire ses sources, sauf celles qu'un autre
  // besoin actif réclame encore. « Tout » et « Rien » restent des raccourcis.
  const cumulables = SOURCE_PRESETS.filter((pr) => !["tout", "rien"].includes(pr.id));
  const picks = Object.fromEntries(cumulables.map((pr) => [pr.id, pr.pick(providers)]));
  const isActive = (pr) => picks[pr.id].length > 0 && picks[pr.id].every((n) => selected.includes(n));
  const toggleWant = (pr) => {
    if (!isActive(pr)) { onChange(Array.from(new Set([...selected, ...picks[pr.id]]))); return; }
    const encoreVoulues = new Set(cumulables.filter((o) => o.id !== pr.id && isActive(o)).flatMap((o) => picks[o.id]));
    onChange(selected.filter((n) => !picks[pr.id].includes(n) || encoreVoulues.has(n)));
  };
  return (
    <div className="mb-4" data-testid="source-picker">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs text-slate-500">Je veux :</span>
        {cumulables.map((pr) => {
          const on = isActive(pr);
          return (
            <button key={pr.id} type="button" data-testid={`source-preset-${pr.id}`} onClick={() => toggleWant(pr)} aria-pressed={on}
              className={`rounded-full border px-2.5 py-1 text-[12px] ${on ? "border-indigo-400 bg-indigo-50 text-indigo-800 font-medium" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-700"}`}>
              {on ? "✓ " : ""}{pr.label}
            </button>
          );
        })}
        <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden="true" />
        <button type="button" data-testid="source-preset-tout" onClick={() => onChange(providers.filter((p) => p.enabled).map((p) => p.name))}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[12px] text-slate-600 hover:border-indigo-300">Tout</button>
        <button type="button" data-testid="source-preset-rien" onClick={() => onChange([])}
          className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[12px] text-slate-600 hover:border-indigo-300">Rien</button>
        <span className="ml-auto text-[11.5px] text-slate-400" data-testid="source-count">{selected.length} source{selected.length > 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-2">
        {byCat.map((c) => {
          const actives = c.items.filter((p) => p.enabled);
          const n = actives.filter((p) => selected.includes(p.name)).length;
          const all = actives.length > 0 && n === actives.length;
          return (
            <div key={c.id} className="rounded-lg border border-slate-200" data-testid={`source-category-${c.id}`}>
              <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/70 px-2.5 py-1.5">
                <input type="checkbox" data-testid={`source-category-toggle-${c.id}`} checked={all} ref={(el) => { if (el) el.indeterminate = n > 0 && !all; }}
                  onChange={() => setCategory(c.items, !all)} title={all ? "Tout retirer" : "Tout prendre"} />
                <span className="text-[12.5px] font-semibold text-slate-700">{c.label}</span>
                <span className="text-[11px] text-slate-400">{n}/{actives.length}</span>
                <span className="ml-auto hidden text-[11px] text-slate-400 sm:inline" title={c.hint}>{c.hint}</span>
              </div>
              <div className="divide-y divide-slate-50">
                {c.items.map((p) => {
                  const Icon = PROVIDER_META[p.name]?.icon || Building2;
                  const off = !p.enabled;
                  return (
                    <label key={p.name} data-testid={`provider-${p.name}`} title={p.note}
                      className={`flex items-center gap-2.5 px-2.5 py-2 ${off ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
                      <input type="checkbox" disabled={off} checked={selected.includes(p.name)} onChange={() => toggle(p.name)} />
                      <Icon size={15} className="shrink-0 text-indigo-500" />
                      <span className="min-w-0 flex-1 text-[13px] text-slate-700">
                        {p.label}
                        {p.requires_key && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-amber-50 px-1 text-[10px] text-amber-700"><KeyRound size={10} /> clé requise</span>}
                      </span>
                      <span className="hidden flex-wrap justify-end gap-1 sm:flex">
                        {(p.fields || []).slice(0, 4).map((f) => (
                          <span key={f.field} className={`rounded px-1.5 py-0.5 text-[10px] ${NIVEAU_CLS[f.niveau] || "bg-slate-100 text-slate-500"}`} title={`Confiance ${f.niveau}`}>{f.label}</span>
                        ))}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Choix d'entreprises à la main : recherche, puis pastilles retirables.
// ---------------------------------------------------------------------------
function CompanyPicker({ picked, onChange, max }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/companies", { params: { search: term, limit: 8, page: 1 } })
        .then(({ data }) => { if (!cancelled) setResults(data.items || []); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);
  const add = (c) => {
    if (picked.some((p) => p.id === c.id)) return;
    if (picked.length >= max) { toast.error(`${max} entreprises maximum par lancement`); return; }
    onChange([...picked, { id: c.id, nom: c.nom, ville: c.ville, telephone: c.telephone, email: c.email, site_web: c.site_web }]);
    setQ(""); setResults([]);
  };
  const remove = (id) => onChange(picked.filter((p) => p.id !== id));
  return (
    <div className="mb-4 rounded-lg border border-slate-200 p-3" data-testid="company-picker">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
        <input data-testid="company-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, SIRET ou ville…"
          className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm outline-none focus:border-indigo-500" />
        {(results.length > 0 || searching) && (
          <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
            {searching && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Recherche…</div>}
            {results.map((c) => (
              <button key={c.id} type="button" data-testid={`company-pick-${c.id}`} onClick={() => add(c)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40"
                disabled={picked.some((p) => p.id === c.id)}>
                <Building2 size={14} className="shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate text-slate-800">{c.nom}</span>
                <span className="text-xs text-slate-400">{c.ville}</span>
                <span className="flex gap-1 text-slate-300">
                  {c.telephone && <Phone size={11} className="text-emerald-500" />}
                  {c.email && <Mail size={11} className="text-sky-500" />}
                  {c.site_web && <Globe size={11} className="text-amber-500" />}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="company-picked">
        {picked.length === 0 && <span className="text-xs text-slate-400">Cherchez une entreprise et ajoutez-la ; vous pouvez en cumuler plusieurs.</span>}
        {picked.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[12px] text-indigo-800">
            {c.nom}{c.ville && <span className="text-indigo-400"> · {c.ville}</span>}
            <button type="button" onClick={() => remove(c.id)} aria-label={`Retirer ${c.nom}`} className="ml-0.5 text-indigo-400 hover:text-indigo-800"><X size={12} /></button>
          </span>
        ))}
        {picked.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="text-[12px] text-slate-500 underline">Tout retirer</button>
        )}
      </div>
    </div>
  );
}
